/** Analytical technical-entry context. Mock only. Descriptive, not a directive. */
import { KeyRow, MockBadge, SectionTitle, WorkspacePanel } from "@/workspace/primitives";
import { technicalEntryContext } from "@/workspace/mockTickerData";

function Line({ label, children }) {
  return (
    <div className="text-[11px] leading-snug">
      <span className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>{label}</span>
      <div style={{ color: "var(--hoip-text-secondary)" }}>{children}</div>
    </div>
  );
}

export function IncomeEntryContextCard({ ticker }) {
  const ctx = technicalEntryContext(ticker.ticker);
  if (!ctx) return null;
  return (
    <WorkspacePanel
      title="Income Entry Context"
      subtitle="Analytical context only — not an executable claim"
      notice={<MockBadge />}
      hint="EMA relationships, Bollinger location and relative strength are fixtures derived from the mock series. They do not override earnings, quotes, liquidity or selection rules."
      testid="income-entry-context"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Line label="Daily trend">{ctx.daily.line}{ctx.daily.daysSince != null ? ` · ${ctx.daily.daysSince}d since crossover` : ""}</Line>
        <Line label="Weekly trend">{ctx.weekly.line}</Line>
        <Line label="Mean-reversion">{ctx.meanRev.rangeLabel}{ctx.meanRev.rsi != null ? ` · RSI ${ctx.meanRev.rsi}` : ""}</Line>
        <Line label="Regime">{ctx.regime} · confirmed</Line>
        <Line label="Premium environment">{ctx.premium.label}</Line>
        <Line label="Relative">{ctx.relative.spyLabel} · {ctx.relative.sectorLabel}</Line>
      </div>
      <p className="text-[11px] mt-2.5 leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }} data-testid="income-entry-decision">
        {ctx.decision}
      </p>
      <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
        Context tag: {ctx.tag}. Not labelled as an entry command.
      </p>
    </WorkspacePanel>
  );
}

export function TechnicalEntryStrip({ ticker, dte, conflictLabel }) {
  const ctx = technicalEntryContext(ticker.ticker);
  if (!ctx) return null;
  return (
    <WorkspacePanel
      title="Technical Entry Context"
      subtitle="Analytical context only — does not make a structure executable"
      testid="technical-entry-strip"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-[11px]" style={{ color: "var(--hoip-text-secondary)" }}>
        <Line label="Trend">{ctx.daily.line}</Line>
        <Line label="Weekly">{ctx.weekly.line}</Line>
        <Line label="Range location">{ctx.meanRev.bbPctLabel} · RSI {ctx.meanRev.rsi ?? "—"}</Line>
        <Line label="Regime">{ctx.regime} · confirmed</Line>
        <Line label="Premium">{ctx.premium.label}</Line>
        <Line label="Selected expiry">{dte ? `${dte} DTE` : "—"} · {conflictLabel || "—"}</Line>
      </div>
      <div className="mt-2 text-[11px]" data-testid="technical-entry-tag">
        Context: {ctx.tag}
      </div>
      <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
        Analytical context only. Earnings, quote quality, liquidity and policy still describe product action.
      </p>
    </WorkspacePanel>
  );
}

export function TechnicalEmaDetail({ ticker, mode }) {
  const ctx = technicalEntryContext(ticker.ticker);
  if (!ctx) return null;
  const daily = mode === "weekly" ? ctx.weekly : ctx.daily;
  return (
    <div className="space-y-1" data-testid="tech-ema-detail">
      {mode === "daily" ? (
        <>
          <KeyRow label="EMA 9">{ctx.daily.ema9?.toFixed(2) ?? "Not available in this output"}</KeyRow>
          <KeyRow label="EMA 20">{ctx.daily.ema20?.toFixed(2) ?? "Not available in this output"}</KeyRow>
          <KeyRow label="State">{ctx.daily.line}</KeyRow>
          <KeyRow label="Days since crossover">{ctx.daily.daysSince ?? "—"}</KeyRow>
        </>
      ) : (
        <>
          <KeyRow label="EMA 10">{ctx.weekly.ema10?.toFixed(2) ?? "Not available in this output"}</KeyRow>
          <KeyRow label="EMA 20">{ctx.weekly.ema20?.toFixed(2) ?? "Not available in this output"}</KeyRow>
          <KeyRow label="State">{ctx.weekly.line}</KeyRow>
        </>
      )}
      <div className="pt-1.5 mt-1.5 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
        <SectionTitle>Mean-reversion / range</SectionTitle>
        <KeyRow label="Bollinger percentile">{ctx.meanRev.bbPctLabel}</KeyRow>
        <KeyRow label="Distance to lower band">
          {ctx.meanRev.distLower == null ? "Not available in this output" : `${ctx.meanRev.distLower.toFixed(2)}%`}
        </KeyRow>
        <KeyRow label="Distance to upper band">
          {ctx.meanRev.distUpper == null ? "Not available in this output" : `${ctx.meanRev.distUpper.toFixed(2)}%`}
        </KeyRow>
        <KeyRow label="RSI">{ctx.meanRev.rsi ?? "Not available in this output"}</KeyRow>
        <KeyRow label="ATR (14)">
          {ctx.meanRev.atr == null ? "Not available in this output" : ctx.meanRev.atr.toFixed(2)}
        </KeyRow>
      </div>
      <div className="pt-1.5 mt-1.5 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
        <SectionTitle>Relative context</SectionTitle>
        <KeyRow label="vs SPY">{ctx.relative.spyLabel}</KeyRow>
        <KeyRow label="vs sector">{ctx.relative.sectorLabel}</KeyRow>
      </div>
      <p className="text-[10px] mt-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>
        {daily.line}. EMA supplements SMA 20/50/200; it does not replace them.
      </p>
    </div>
  );
}
